import React from 'react';

import { Rate } from 'antd';
import styled from 'styled-components';

import { WIDGET_TYPES } from 'common/Widgets/constants';
import { formatValueDifference } from 'common/Widgets/valueDifferenceUtils';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 11px;
  min-width: 120px;
`;

const Row = styled.div<{ isOverall: boolean }>`
  display: flex;
  flex-flow: row;
  gap: 6px;
  height: 38px;
`;

const Name = styled.div`
  font-size: 12px;
  text-transform: uppercase;
  font-family: var(--font-bold);
  letter-spacing: 0.5px;
  max-width: 140px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const RatingValue = styled.div`
  font-size: 30px;
  line-height: 30px;
  margin-top: 12px;

  &.isOverall {
    font-size: 42px;
    line-height: 42px;
    margin-top: 0;
  }
`;

const RightSide = styled.div`
  display: flex;
  flex-flow: column;
  justify-content: space-between;
`;

const Difference = styled.span<{ isPositive: boolean; isOverall: boolean }>`
  font-size: ${(props) => (props.isOverall ? '20px' : '16px')};
  color: ${(props) => (props.isPositive ? 'var(--negative-orange)' : 'var(--positive-green)')};
`;

const StyledStar = styled(Rate)`
  .ant-rate-star:not(:last-child) {
    margin-right: 3px !important;
  }
`;

interface RatingCardProps {
  name: string;
  rating: number | null;
  difference?: number | null;
  valueDisplay?: number;
}

export const RatingCard: React.FC<RatingCardProps> = ({ name, rating, difference, valueDisplay }) => {
  const isOverall = name === 'OVERALL';
  const testIdName = name.toLowerCase().replace(/\s+/g, '');

  return (
    <Container>
      <Name title={name}>{name}</Name>
      <Row isOverall={isOverall}>
        <RatingValue className={isOverall ? 'isOverall' : ''} data-testid={`rs-widget-${testIdName}-rating`}>
          {rating?.toFixed(1) ?? 'N/A'}
        </RatingValue>
        <RightSide>
          <StyledStar
            allowHalf
            value={rating ?? 0}
            disabled
            style={{ fontSize: '10px', color: 'var(--gray-dark)' }}
          />
          {difference !== undefined && difference !== null && difference !== 0 && (
            <Difference
              isPositive={difference >= 0}
              isOverall={isOverall}
              data-testid={`rs-widget-${testIdName}-rating-difference`}
            >
              {formatValueDifference(difference, WIDGET_TYPES.RATING, valueDisplay)}
            </Difference>
          )}
        </RightSide>
      </Row>
    </Container>
  );
};

export default RatingCard;
